'use client';

import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { Toaster as Sonner, toast } from 'sonner';
import { RECORDING_MINI_BAR_HEIGHT_PX } from '@/lib/constants';
import { useIsMiniBarVisible } from '@/lib/stores';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * The breathing room between the toast stack and the bottom of the viewport,
 * matching sonner's own `VIEWPORT_OFFSET` / `MOBILE_VIEWPORT_OFFSET`.
 *
 * Restated only because sonner does not export them, and `offset` replaces the
 * default outright rather than adding to it. We pass `offset` ONLY while the
 * recording mini-bar is up (below), so sonner's own values stay authoritative
 * for every other render.
 */
const VIEWPORT_GAP_PX = 24;
const VIEWPORT_GAP_MOBILE_PX = 16;

/**
 * Anchor the close button to the toast's top-RIGHT corner (LF-6246). Sonner
 * ships it top-left for LTR — these are the values its own stylesheet applies
 * under `[dir=rtl]`, set inline so they win over the `[dir=ltr]` rule and
 * cascade down to `[data-close-button]`.
 */
const CLOSE_BUTTON_TOP_RIGHT = {
  '--toast-close-button-start': 'unset',
  '--toast-close-button-end': '0',
  '--toast-close-button-transform': 'translate(35%, -35%)'
} as React.CSSProperties;

/**
 * Sit the status icon on the FIRST line of the message instead of centred against
 * the whole block. Sonner lays the toast out as `align-items: center`, so on a toast
 * with a description — or any message that wraps — the tick floats level with the
 * middle of the text rather than with the line it belongs to.
 *
 * `self-start` opts just the icon out of that centring; the `2px` nudge then centres
 * its 16px box on the first line, whose box is 19.5px (sonner's 13px font × its 1.5
 * title line-height). Both numbers are literals in sonner's own stylesheet, so the
 * offset is written as a literal too — `mt-0.5` would land in the same place today
 * but drift the moment the `--spacing` density knob moves.
 *
 * Unopposed, so no `!` needed: sonner sets `align-items` and the horizontal margins
 * on `[data-icon]`, never `align-self` or `margin-top`.
 */
const ICON_ON_FIRST_LINE =
  '[&_[data-icon]]:mt-[2px] [&_[data-icon]]:self-start';

/**
 * Keep the message clear of an action/cancel button. Sonner pushes that button hard
 * right (`margin-inline-start: auto`) and puts only its 6px row `gap` between the
 * two, so a message long enough to fill the row reads as if it is sitting on the
 * button. This widens that channel to 14px.
 *
 * `:has([data-button])` scopes it to toasts that actually carry one — the padding
 * eats into the wrap width, and there is no reason to narrow the ~324px of text on
 * the many toasts with no button at all. Both the action and the cancel button carry
 * `data-button`, so both are covered.
 *
 * Unopposed (sonner sets only `display`/`flex-direction`/`gap` on `[data-content]`),
 * so no `!` needed.
 */
const MESSAGE_CLEAR_OF_BUTTON =
  '[&:has([data-button])_[data-content]]:pr-[8px]';

/** Whether the event target is a field the user is typing into. */
function isTypingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  );
}

/**
 * "C" clears every visible toast. `toast.dismiss()` with no id ignores both the
 * toast type and `dismissible`, so this reaches even a `loading` toast that a
 * call site pinned open with `duration: Infinity` — the case with no other exit.
 */
function useDismissAllShortcut() {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'c') return;
      // A modified "C" belongs to the browser or another handler, not to us.
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.isComposing) return;
      if (isTypingInto(event.target)) return;
      toast.dismiss();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();
  useDismissAllShortcut();

  // The same selector the mini-bar's own spacer uses, so the toaster can never
  // disagree with it about whether the bar is on screen.
  const miniBarVisible = useIsMiniBarVisible();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      // Bottom-CENTRE, not sonner's bottom-right default: the right-hand corner
      // is where primary CTAs (form submits) and the app-wide UploadOverlay card
      // live, so a toast there covers the control the user is reaching for.
      position="bottom-center"
      // Lay concurrent toasts out in full instead of sonner's collapsed stack
      // (LF-6129). Collapsed, its stylesheet sets `opacity: 0` on the CONTENT of
      // every toast behind the front one and forces them all to the front
      // toast's height — so a second and third notification render as blank
      // slabs, unreadable until the user happens to hover. A notification the
      // user cannot read has failed at its only job. No effect on a lone toast.
      expand
      // Clear the recording mini-bar while it is up, so a toast never lands on
      // its pause/stop controls (sonner's z-index sits far above the bar's, so
      // it wins the paint and swallows the clicks). Only the `bottom` side is
      // overridden — sonner keeps its own defaults for the other three.
      offset={
        miniBarVisible
          ? { bottom: RECORDING_MINI_BAR_HEIGHT_PX + VIEWPORT_GAP_PX }
          : undefined
      }
      mobileOffset={
        miniBarVisible
          ? { bottom: RECORDING_MINI_BAR_HEIGHT_PX + VIEWPORT_GAP_MOBILE_PX }
          : undefined
      }
      // Renders an X on every toast. `@/lib/toast` additionally guarantees the
      // per-toast half of this — a caller cannot pass `dismissible: false`, and
      // `loading` toasts are re-typed so they qualify for the close button at all.
      closeButton
      style={CLOSE_BUTTON_TOP_RIGHT}
      toastOptions={{
        classNames: {
          toast: `group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg ${ICON_ON_FIRST_LINE} ${MESSAGE_CLEAR_OF_BUTTON}`,
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground'
        }
      }}
      {...props}
    />
  );
};

export { Toaster };
